import React from "react";
import { motion } from "framer-motion";
import {
  FaArrowRight,
  FaLightbulb,
  FaSearch,
  FaBullhorn,
  FaShareAlt,
  FaEnvelopeOpenText,
  FaChartLine,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import "./Services.css";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

const item = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};

const services = [
  {
    id: "digital-strategy",
    tag: "Plan",
    title: "Strategy & consulting",
    desc: "Goals, audience, budget, and channel priorities aligned to your business—delivered as workshops, audits, or retainers and scoped in writing.",
    icon: <FaLightbulb />,
    bullets: ["Market & competitor review", "KPI definitions", "Roadmaps & pilots"],
  },
  {
    id: "seo-content",
    tag: "Organic",
    title: "SEO & content marketing",
    desc: "Technical hygiene, keyword research, content briefs, and on-page structure. Search engines control rankings—we do not guarantee positions.",
    icon: <FaSearch />,
    bullets: ["Site audits", "Content calendars", "Internal linking plans"],
  },
  {
    id: "paid-media",
    tag: "Paid",
    title: "Paid search & social",
    desc: "Google Ads, Meta, LinkedIn, and other platforms where they fit. Conversion tracking, creative testing, and budget guardrails included.",
    icon: <FaBullhorn />,
    bullets: ["Campaign build & QA", "Audience & bid strategy", "Ongoing optimization"],
  },
  {
    id: "social-media",
    tag: "Social",
    title: "Social media management",
    desc: "Editorial calendars, community guidelines, creative coordination, and reporting—aligned with brand voice and compliance needs.",
    icon: <FaShareAlt />,
    bullets: ["Organic + paid alignment", "UGC & rights reminders", "Crisis-aware tone"],
  },
  {
    id: "email-marketing",
    tag: "CRM",
    title: "Email & lifecycle",
    desc: "List hygiene, segmentation, automation flows, and CAN-SPAM / GDPR-aware practices. Regulated industries should involve counsel.",
    icon: <FaEnvelopeOpenText />,
    bullets: ["Welcome & nurture", "Promotions & newsletters", "Deliverability checks"],
  },
  {
    id: "analytics-cro",
    tag: "Measure",
    title: "Analytics & CRO",
    desc: "GA4, tag manager, dashboards, and landing-page tests. We improve clarity and conversion paths—not promise fixed lift percentages.",
    icon: <FaChartLine />,
    bullets: ["Event mapping", "Funnel reporting", "A/B test planning"],
  },
];

const Services = () => {
  const navigate = useNavigate();

  return (
    <section className="srv2" aria-labelledby="services-heading">
      <div className="srv2__wrap">
        <motion.div
          className="srv2__head"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.65 }}
        >
          <p className="srv2__eyebrow">Service catalog</p>
          <h2 id="services-heading" className="srv2__title">
            Digital marketing services built for clarity
          </h2>
          <p className="srv2__sub">
            Choose one offering or combine several. Each opens a detailed overview. Final scope, fees, and timelines are
            confirmed in a proposal or statement of work—not on this page alone.
          </p>
        </motion.div>

        <motion.div
          className="srv2__grid"
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.12 }}
        >
          {services.map((s) => (
            <motion.article
              key={s.id}
              className="srv2__card"
              variants={item}
              onClick={() => navigate(`/services/${s.id}`)}
              onKeyDown={(e) => e.key === "Enter" && navigate(`/services/${s.id}`)}
              role="link"
              tabIndex={0}
              aria-label={`${s.title} — view details`}
            >
              <div className="srv2__top">
                <div className="srv2__icon" aria-hidden="true">
                  {s.icon}
                </div>
                <span className="srv2__tag">{s.tag}</span>
              </div>
              <h3 className="srv2__h3">{s.title}</h3>
              <p className="srv2__p">{s.desc}</p>
              <ul className="srv2__bullets">
                {s.bullets.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
              <span className="srv2__link">
                Service details <FaArrowRight aria-hidden />
              </span>
            </motion.article>
          ))}
        </motion.div>

        <motion.aside
          className="srv2__note"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <p>
            <strong>Results vary.</strong> Performance depends on your offer, budget, competition, creative, tracking
            quality, and platform policies. We do not claim fixed ROI, impression share, or ranking positions. Affiliate
            or referral relationships are disclosed where required.
          </p>
        </motion.aside>
      </div>
    </section>
  );
};

export default Services;
